// H5 对账结算（TDesign）：
// 上半部分「对账结算」：按兼职聚合统计 审核通过且未对账 的数据（进群时间/审核时间筛选，默认空=全部），勾选兼职后生成对账函；
// 下半部分「对账函」：已生成对账函列表，按生成时间倒序，支持 查看关联数据 / 撤回（必填原因）。
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Space, Select, DatePicker, Tag, Table, Dialog, Textarea, Input, MessagePlugin } from 'tdesign-react';
import { parttimeApi } from '../../api/parttime';
import { getPageSize } from '../../api/parttimePager';
import ParttimeDataDetailDialog from './ParttimeDataDetailDialog';

// 分页每页条数配置 key（后端全局配置，前端不写死）
const PAGE_SIZE_KEY = 'page_size_H5';

// 对账函 对账状态（agreeStatus）
const AGREE_OPTS = [
  { label: '全部', value: 'ALL' },
  { label: '对账中', value: 'PENDING' },
  { label: '对账通过', value: 'CONFIRMED' },
  { label: '对账未通过', value: 'DISAGREED' },
];
const AGREE_LABEL = { PENDING: '对账中', CONFIRMED: '对账通过', DISAGREED: '对账未通过' };
const agreeTheme = (s) => {
  if (s === 'CONFIRMED') return 'success';
  if (s === 'DISAGREED') return 'danger';
  return 'warning';
};
// 处理状态（对账函下数据列表用，同数据核对）
const statusLabel = {
  UNASSIGNED: '未分配', PRE_ASSIGNED: '已分配未发送', SENT: '已发送未领取', CLAIMED: '已领取进行中',
  SUBMITTED: '已截图待审核', PASSED: '截图审核通过', FAILED: '截图审核不通过',
  RECALLED: '分配后撤回', REJECTED: '分配后拒绝',
};
const statusTheme = (s) => {
  if (s === 'PASSED') return 'success';
  if (s === 'FAILED' || s === 'REJECTED') return 'danger';
  if (s === 'CLAIMED') return 'primary';
  if (s === 'SUBMITTED' || s === 'SENT' || s === 'PRE_ASSIGNED' || s === 'RECALLED') return 'warning';
  return 'default';
};
// 数据行对账状态（中文文案 + 主题色）
const SETTLE_LABEL = { UNREVIEWED: '未对账', SETTLEMENTING: '对账中', CONFIRMED: '对账通过', DISAGREED: '对账未通过' };
const settleTheme = (s) => {
  if (s === 'CONFIRMED') return 'success';
  if (s === 'DISAGREED') return 'danger';
  if (s === 'SETTLEMENTING') return 'warning';
  return 'default';
};

/**
 * H5 对账结算页面
 * 上半部分：对账结算（按兼职统计 审核通过且未对账，勾选兼职生成对账函，后端分页）
 * 下半部分：对账函（已生成对账函倒序列表，后端分页，支持查看/撤回）
 */
export default function ParttimeSettlement() {
  // ===== 上半部分：对账结算（按兼职聚合统计） =====
  const freshStatsFilters = () => ({ groupStart: '', groupEnd: '', reviewStart: '', reviewEnd: '' });
  const [statsFilters, setStatsFilters] = useState(freshStatsFilters()); // 输入区实时值
  const [statsQ, setStatsQ] = useState(freshStatsFilters());             // 点击查询后应用的条件
  const [statsRows, setStatsRows] = useState([]);
  const [statsTotal, setStatsTotal] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsPage, setStatsPage] = useState(1);
  const [statsPageSize, setStatsPageSize] = useState(50);
  const [statsSel, setStatsSel] = useState(() => new Set());             // 勾选的兼职 userId
  // 查询强制刷新标记（上下两个区块各一个）：保证无搜索条件时点击「查询」也能触发接口（避免 setState 相同引用被 React 跳过）
  const [statsReloadKey, setStatsReloadKey] = useState(0);
  const [sReloadKey, setSReloadKey] = useState(0);
  // 请求中标记：生成对账函 / 撤回 按钮 loading，防止重复提交
  const [generating, setGenerating] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  // ===== 下半部分：对账函列表 =====
  const freshSettleFilters = () => ({ settleStatus: 'ALL', assignName: '' });
  const [sFilters, setSFilters] = useState(freshSettleFilters()); // 输入区实时值
  const [sQ, setSQ] = useState(freshSettleFilters());             // 点击查询后应用的条件
  const [sRows, setSRows] = useState([]);
  const [sTotal, setSTotal] = useState(0);
  const [sLoading, setSLoading] = useState(false);
  const [sPage, setSPage] = useState(1);
  const [sPageSize, setSPageSize] = useState(50);

  // 弹窗状态
  const [statsView, setStatsView] = useState(null);   // 上半部分行内「查看」：该兼职未对账数据列表 { userId, name, rows, total, page }
  const [settleView, setSettleView] = useState(null); // 下半部分「查看」：对账函关联数据 { settlementId, name, rows }
  const [wd, setWd] = useState(null);                 // 撤回原因弹窗 { settlementId }
  const [wdReason, setWdReason] = useState('');
  const [detailRow, setDetailRow] = useState(null);   // 行内数据「查看」→ 共享数据详情弹窗

  // 装载分页配置（后端读取每页条数）
  useEffect(() => { getPageSize(PAGE_SIZE_KEY).then(setStatsPageSize).catch(() => {}); }, []);
  useEffect(() => { getPageSize(PAGE_SIZE_KEY).then(setSPageSize).catch(() => {}); }, []);

  const fmtDt = (d) => (d ? String(d).replace('T', ' ').slice(0, 19) : '');
  const fmtD = (d) => (d ? String(d).slice(0, 10) : '');
  // 小字（灰色）展示筛选条件文本
  const sub = (t) => (t ? <span style={{ fontSize: 11, color: '#999' }}>{t}</span> : '');
  // 合并显示数量与单价：如 2个x2元；无单价时只显示数量
  const merged = (count, price) => (price ? `${count}个x${price}元` : `${count}个`);

  // ===== 上半部分：加载按兼职聚合统计 =====
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = { page: statsPage, size: statsPageSize };
      if (statsQ.groupStart) params.groupStart = statsQ.groupStart;
      if (statsQ.groupEnd) params.groupEnd = statsQ.groupEnd;
      if (statsQ.reviewStart) params.reviewStart = statsQ.reviewStart;
      if (statsQ.reviewEnd) params.reviewEnd = statsQ.reviewEnd;
      const res = await parttimeApi.settleStats(params);
      setStatsRows(res.rows || []);
      setStatsTotal(res.total || 0);
    } catch (e) { MessagePlugin.error(e.message); } finally { setStatsLoading(false); }
  }, [statsQ, statsPage, statsPageSize, statsReloadKey]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // ===== 下半部分：加载对账函列表 =====
  const loadSettlements = useCallback(async () => {
    setSLoading(true);
    try {
      const params = { page: sPage, size: sPageSize };
      if (sQ.settleStatus && sQ.settleStatus !== 'ALL') params.settleStatus = sQ.settleStatus;
      if (sQ.assignName && String(sQ.assignName).trim()) params.assignName = String(sQ.assignName).trim();
      const res = await parttimeApi.settlements(params);
      setSRows(res.rows || []);
      setSTotal(res.total || 0);
    } catch (e) { MessagePlugin.error(e.message); } finally { setSLoading(false); }
  }, [sQ, sPage, sPageSize, sReloadKey]);
  useEffect(() => { loadSettlements(); }, [loadSettlements]);

  // 上半部分：生成对账函（勾选兼职 + 当前筛选条件）
  const genByUsers = async () => {
    if (!statsSel.size) { MessagePlugin.warning('请先勾选兼职'); return; }
    setGenerating(true);
    try {
      const n = await parttimeApi.generateByUsers([...statsSel], statsQ.groupStart, statsQ.groupEnd, statsQ.reviewStart, statsQ.reviewEnd);
      MessagePlugin.success(`已生成 ${n} 个兼职的对账函`);
      setStatsSel(new Set());
      loadStats();
      loadSettlements();
    } catch (e) { MessagePlugin.error(e.message); } finally { setGenerating(false); }
  };

  // 下半部分：撤回对账函（必填原因）
  const confirmWithdraw = async () => {
    if (!String(wdReason).trim()) { MessagePlugin.error('请输入撤回原因'); return; }
    setWithdrawing(true);
    try {
      await parttimeApi.withdrawSettlement(wd.settlementId, wdReason.trim());
      MessagePlugin.success('已撤回');
      setWd(null);
      loadStats();        // 撤回后数据回未对账，上半部分统计同步刷新
      loadSettlements();
    } catch (e) { MessagePlugin.error(e.message); } finally { setWithdrawing(false); }
  };

  // 上半部分：查看某兼职 未对账数据列表（分页拉取）
  const openStatsView = async (userId, name, page) => {
    try {
      // 仅传非空筛选条件，避免序列化为 groupStart=undefined 导致后端日期解析失败
      const params = { page: page || 1, size: 20 };
      if (statsQ.groupStart) params.groupStart = statsQ.groupStart;
      if (statsQ.groupEnd) params.groupEnd = statsQ.groupEnd;
      if (statsQ.reviewStart) params.reviewStart = statsQ.reviewStart;
      if (statsQ.reviewEnd) params.reviewEnd = statsQ.reviewEnd;
      const d = await parttimeApi.settlementStatsData(userId, params);
      setStatsView({ userId, name, rows: d.rows || [], total: d.total || 0, page: page || 1, pageSize: 20 });
    } catch (e) { MessagePlugin.error(e.message); }
  };

  // 下半部分：查看对账函关联数据
  const openSettleView = async (settlementId, name) => {
    try {
      const d = await parttimeApi.settlementData(settlementId);
      setSettleView({ settlementId, name, rows: d.dataList || [], ...d });
    } catch (e) { MessagePlugin.error(e.message); }
  };

  // 行内数据「查看」：共享数据详情弹窗（与数据核对一致）
  const openDetail = async (dataId) => {
    try {
      setDetailRow(await parttimeApi.dataDetailAdmin(dataId));
    } catch (e) { MessagePlugin.error(e.message); }
  };

  // ---- 上半部分：TDesign Table 列定义（每行一个兼职的未对账统计） ----
  const statsColumns = [
    { colKey: 'row-select', type: 'multiple', width: 48 },
    { colKey: 'name', title: '兼职', width: 120, cell: ({ row }) => <Tag theme="primary" variant="light">{row.name}</Tag> },
    { colKey: 'passAdd', title: '通过添加', cell: ({ row }) => merged(row.passAdd, row.addPriceList) },
    { colKey: 'passGroup', title: '通过进群', cell: ({ row }) => merged(row.passGroup, row.groupPriceList) },
    { colKey: 'failAdd', title: '未过添加', cell: ({ row }) => row.failAdd },
    { colKey: 'failGroup', title: '未过进群', cell: ({ row }) => row.failGroup },
    { colKey: 'totalWage', title: '总金额', cell: ({ row }) => <span style={{ fontWeight: 600 }}>{row.totalWage}</span> },
    { colKey: 'op', title: '操作', width: 90, cell: ({ row }) => (
      <Button size="small" theme="default" variant="text" onClick={() => openStatsView(row.userId, row.name, 1)}>查看</Button>
    ) },
  ];

  // ---- 下半部分：TDesign Table 列定义（每行一个对账函） ----
  const settleColumns = [
    { colKey: 'name', title: '兼职', width: 110, cell: ({ row }) => <Tag theme="primary" variant="light">{row.name}</Tag> },
    { colKey: 'groupRange', title: '进群时间(筛选条件)', width: 160, cell: ({ row }) => (row.groupStartDate ? sub(`${fmtD(row.groupStartDate)} ~ ${fmtD(row.groupEndDate)}`) : '') },
    { colKey: 'reviewRange', title: '审核时间(筛选条件)', width: 160, cell: ({ row }) => (row.reviewStartDate ? sub(`${fmtD(row.reviewStartDate)} ~ ${fmtD(row.reviewEndDate)}`) : '') },
    { colKey: 'addCount', title: '通过添加', cell: ({ row }) => merged(row.addCount, row.addPriceList) },
    { colKey: 'groupCount', title: '通过进群', cell: ({ row }) => merged(row.groupCount, row.groupPriceList) },
    { colKey: 'addFailCount', title: '未过添加', cell: ({ row }) => row.addFailCount },
    { colKey: 'groupFailCount', title: '未过进群', cell: ({ row }) => row.groupFailCount },
    { colKey: 'totalWage', title: '总金额', cell: ({ row }) => <span style={{ fontWeight: 600 }}>{row.totalWage}</span> },
    { colKey: 'agreeStatus', title: '对账状态', width: 100, cell: ({ row }) => <Tag theme={agreeTheme(row.agreeStatus)} variant="light">{AGREE_LABEL[row.agreeStatus] || row.agreeStatus || ''}</Tag> },
    { colKey: 'withdrawReason', title: '客服撤回原因', width: 120, cell: ({ row }) => <span title={row.withdrawReason || ''} style={{ display: 'block', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.withdrawReason || ''}</span> },
    { colKey: 'disagreeReason', title: '兼职拒绝原因', width: 120, cell: ({ row }) => <span title={row.disagreeReason || ''} style={{ display: 'block', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.disagreeReason || ''}</span> },
    { colKey: 'createTime', title: '生成时间', width: 160, cell: ({ row }) => fmtDt(row.createTime) },
    { colKey: 'op', title: '操作', width: 120, cell: ({ row }) => (
      <Space size={4}>
        <Button size="small" theme="default" variant="text" onClick={() => openSettleView(row.settlementId, row.name)}>查看</Button>
        {row.agreeStatus === 'PENDING' && row.status === 'SENT' && (
          <Button size="small" theme="danger" variant="text" onClick={() => { setWdReason(''); setWd({ settlementId: row.settlementId }); }}>撤回</Button>
        )}
      </Space>
    ) },
  ];

  // ---- 查看对账函关联数据列表列（同数据核对） ----
  const settleColumnsData = [
    { colKey: 'ownerName', title: '业主姓名', cell: ({ row }) => row.ownerName || '' },
    { colKey: 'phone', title: '手机号', cell: ({ row }) => row.phone || '' },
    { colKey: 'wechat', title: '微信号', cell: ({ row }) => row.wechat || '' },
    { colKey: 'city', title: '城市', cell: ({ row }) => row.city || '' },
    { colKey: 'community', title: '小区', ellipsis: true, cell: ({ row }) => row.community || '' },
    { colKey: 'assignName', title: '兼职人员', cell: ({ row }) => row.assignName || '' },
    { colKey: 'addWageSnapshot', title: '添加单价', cell: ({ row }) => (row.addWageSnapshot != null ? `${row.addWageSnapshot}元` : '') },
    { colKey: 'groupWageSnapshot', title: '进群单价', cell: ({ row }) => (row.groupWageSnapshot != null ? `${row.groupWageSnapshot}元` : '') },
    { colKey: 'addCount', title: '添加截图', cell: ({ row }) => (row.addCount ? (
      <div style={{ lineHeight: 1.5 }}>
        <div style={{ fontWeight: 600 }}>x{row.addCount}</div>
        <div style={{ fontSize: 11, color: '#999' }}>{fmtDt(row.addTime)}</div>
      </div>
    ) : null) },
    { colKey: 'groupCount', title: '进群截图', cell: ({ row }) => (row.groupCount ? (
      <div style={{ lineHeight: 1.5 }}>
        <div style={{ fontWeight: 600 }}>x{row.groupCount}</div>
        <div style={{ fontSize: 11, color: '#999' }}>{fmtDt(row.groupTime)}</div>
      </div>
    ) : null) },
    { colKey: 'status', title: '处理状态', cell: ({ row }) => <Tag theme={statusTheme(row.status)} variant="light">{statusLabel[row.status] || row.status || ''}</Tag> },
    { colKey: 'settleStatus', title: '对账状态', cell: ({ row }) => <Tag theme={settleTheme(row.settleStatus)} variant="light">{SETTLE_LABEL[row.settleStatus] || row.settleStatus || ''}</Tag> },
    { colKey: 'op', title: '操作', width: 80, cell: ({ row }) => <Button size="small" theme="default" variant="text" onClick={() => openDetail(row.dataId)}>查看</Button> },
  ];

  // ---- 查看某兼职 未对账数据列表列 ----
  const statsViewColumns = [
    { colKey: 'ownerName', title: '业主姓名', cell: ({ row }) => row.ownerName || '' },
    { colKey: 'phone', title: '手机号', cell: ({ row }) => row.phone || '' },
    { colKey: 'wechat', title: '微信号', cell: ({ row }) => row.wechat || '' },
    { colKey: 'city', title: '城市', cell: ({ row }) => row.city || '' },
    { colKey: 'community', title: '小区', ellipsis: true, cell: ({ row }) => row.community || '' },
    { colKey: 'addWageSnapshot', title: '添加单价', cell: ({ row }) => (row.addWageSnapshot != null ? `${row.addWageSnapshot}元` : '') },
    { colKey: 'groupWageSnapshot', title: '进群单价', cell: ({ row }) => (row.groupWageSnapshot != null ? `${row.groupWageSnapshot}元` : '') },
    { colKey: 'addCount', title: '添加截图', cell: ({ row }) => (row.addCount ? (
      <div style={{ lineHeight: 1.5 }}>
        <div style={{ fontWeight: 600 }}>x{row.addCount}</div>
        <div style={{ fontSize: 11, color: '#999' }}>{fmtDt(row.addTime)}</div>
      </div>
    ) : null) },
    { colKey: 'groupCount', title: '进群截图', cell: ({ row }) => (row.groupCount ? (
      <div style={{ lineHeight: 1.5 }}>
        <div style={{ fontWeight: 600 }}>x{row.groupCount}</div>
        <div style={{ fontSize: 11, color: '#999' }}>{fmtDt(row.groupTime)}</div>
      </div>
    ) : null) },
    { colKey: 'status', title: '处理状态', cell: ({ row }) => <Tag theme={statusTheme(row.status)} variant="light">{statusLabel[row.status] || row.status || ''}</Tag> },
    { colKey: 'settleStatus', title: '对账状态', cell: ({ row }) => <Tag theme={settleTheme(row.settleStatus)} variant="light">{SETTLE_LABEL[row.settleStatus] || row.settleStatus || ''}</Tag> },
    { colKey: 'op', title: '操作', width: 80, cell: ({ row }) => <Button size="small" theme="default" variant="text" onClick={() => openDetail(row.dataId)}>查看</Button> },
  ];

  // ---- 分页切换（切换每页条数时回到第一页） ----
  const onStatsPageChange = (info) => {
    const size = info && info.pageSize;
    const cur = typeof info === 'number' ? info : (info && info.current ? info.current : statsPage);
    if (size && size !== statsPageSize) {
      setStatsPageSize(size);
      setStatsPage(1);
    } else if (cur) {
      setStatsPage(cur);
    }
  };
  const onSPageChange = (info) => {
    const size = info && info.pageSize;
    const cur = typeof info === 'number' ? info : (info && info.current ? info.current : sPage);
    if (size && size !== sPageSize) {
      setSPageSize(size);
      setSPage(1);
    } else if (cur) {
      setSPage(cur);
    }
  };

  return (
    <div className="pt-app">
      {/* ===== 上半部分：对账结算（按兼职聚合统计） ===== */}
      <div className="pt-card" style={{ padding: 20 }}>
        <p className="pt-search-title">对账结算</p>
        {/* 筛选区：进群时间/审核时间（默认空=全部时间） */}
        <div style={{ paddingBottom: 14, borderBottom: '1px solid #f0f0f0' }}>
          <div className="pt-search-row">
            <span className="pt-search-label">进群时间</span>
            <DatePicker format="YYYY-MM-DD" value={statsFilters.groupStart} onChange={(v) => setStatsFilters(f => ({ ...f, groupStart: v || '' }))} placeholder="开始" style={{ width: 150 }} />
            <DatePicker format="YYYY-MM-DD" value={statsFilters.groupEnd} onChange={(v) => setStatsFilters(f => ({ ...f, groupEnd: v || '' }))} placeholder="结束" style={{ width: 150 }} />
            <span className="pt-search-label">审核时间</span>
            <DatePicker format="YYYY-MM-DD" value={statsFilters.reviewStart} onChange={(v) => setStatsFilters(f => ({ ...f, reviewStart: v || '' }))} placeholder="开始" style={{ width: 150 }} />
            <DatePicker format="YYYY-MM-DD" value={statsFilters.reviewEnd} onChange={(v) => setStatsFilters(f => ({ ...f, reviewEnd: v || '' }))} placeholder="结束" style={{ width: 150 }} />
            <Button theme="primary" variant="text" size="small" loading={statsLoading} onClick={() => { setStatsPage(1); setStatsQ(statsFilters); setStatsReloadKey(k => k + 1); }}>查询</Button>
            <Button theme="default" variant="text" size="small" onClick={() => { const f = freshStatsFilters(); setStatsFilters(f); setStatsQ(f); setStatsPage(1); }}>重置</Button>
          </div>
        </div>

        {/* 操作区：仅「生成对账函」按钮（按勾选兼职生成） */}
        <div style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
          <Space size={12} wrap>
            <Button size="small" theme="primary" variant="text" loading={generating} disabled={generating} onClick={genByUsers}>生成对账函</Button>
            <span style={{ color: '#888' }}>勾选兼职后生成对账函（统计审核通过且未对账数据，生成时记录当前筛选条件）</span>
          </Space>
        </div>

        {/* 列表：按兼职聚合统计（后端分页） */}
        <div style={{ paddingTop: 14 }}>
          <Table
            rowKey="userId"
            resizable
            data={statsRows}
            loading={statsLoading}
            hover
            size="small"
            columns={statsColumns}
            selectedRowKeys={[...statsSel]}
            onSelectChange={(keys) => setStatsSel(new Set(keys))}
            empty={<span>暂无数据</span>}
            pagination={{
              current: statsPage, pageSize: statsPageSize, total: statsTotal,
              onChange: onStatsPageChange,
              totalContent: (t) => `共 ${t} 条数据`,
            }}
          />
        </div>
      </div>

      {/* ===== 下半部分：对账函列表 ===== */}
      <div className="pt-card" style={{ padding: 20, marginTop: 16 }}>
        <p className="pt-search-title">对账函</p>
        {/* 筛选区：兼职人员 + 对账状态 */}
        <div style={{ paddingBottom: 14, borderBottom: '1px solid #f0f0f0' }}>
          <div className="pt-search-row">
            <span className="pt-search-label">兼职人员</span>
            <Input placeholder="姓名/手机号" value={sFilters.assignName} onChange={(v) => setSFilters(f => ({ ...f, assignName: v }))} style={{ width: 180 }} clearable />
            <span className="pt-search-label">对账状态</span>
            <Select value={sFilters.settleStatus} options={AGREE_OPTS} onChange={(v) => setSFilters(f => ({ ...f, settleStatus: v }))} style={{ width: 130 }} />
            <Button theme="primary" variant="text" size="small" onClick={() => { setSPage(1); setSQ(sFilters); setSReloadKey(k => k + 1); }}>查询</Button>
            <Button theme="default" variant="text" size="small" onClick={() => { const f = freshSettleFilters(); setSFilters(f); setSQ(f); setSPage(1); }}>重置</Button>
          </div>
        </div>

        {/* 对账函列表（后端分页，生成时间倒序） */}
        <div style={{ paddingTop: 14 }}>
          <Table
            rowKey="settlementId"
            resizable
            data={sRows}
            loading={sLoading}
            hover
            size="small"
            columns={settleColumns}
            empty={<span>暂无数据</span>}
            pagination={{
              current: sPage, pageSize: sPageSize, total: sTotal,
              onChange: onSPageChange,
              totalContent: (t) => `共 ${t} 条数据`,
            }}
          />
        </div>
      </div>

      {/* 上半部分「查看」：某兼职未对账数据列表弹窗 */}
      <Dialog header={statsView ? `未对账数据 - ${statsView.name}` : '未对账数据'} visible={!!statsView}
        footer={null} width="1300px" closeOnOverlayClick onClose={() => setStatsView(null)}>
        {statsView && (
          <div>
            <Table
              rowKey="dataId"
              resizable
              data={statsView.rows || []}
              hover
              size="medium"
              columns={statsViewColumns}
              empty={<span>暂无数据</span>}
              pagination={{
                current: statsView.page, pageSize: statsView.pageSize, total: statsView.total,
                onChange: (info) => {
                  const size = info && info.pageSize;
                  const cur = typeof info === 'number' ? info : (info && info.current ? info.current : statsView.page);
                  if (size && size !== statsView.pageSize) {
                    setStatsView(v => ({ ...v, page: 1, pageSize: size }));
                    openStatsView(statsView.userId, statsView.name, 1);
                  } else if (cur) {
                    setStatsView(v => ({ ...v, page: cur }));
                    openStatsView(statsView.userId, statsView.name, cur);
                  }
                },
                totalContent: (t) => `共 ${t} 条数据`,
              }}
            />
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <Button theme="default" variant="text" size="small" onClick={() => setStatsView(null)}>关闭</Button>
            </div>
          </div>
        )}
      </Dialog>

      {/* 下半部分「查看」：对账函关联数据列表 */}
      <Dialog header={settleView ? `对账函数据 - ${settleView.name}` : '对账函数据'} visible={!!settleView}
        footer={null} width="1300px" closeOnOverlayClick onClose={() => setSettleView(null)}>
        {settleView && (
          <div>
            <div style={{ marginBottom: 8, color: '#888' }}>
              进群时间：{fmtD(settleView.groupStartDate) || ''} ~ {fmtD(settleView.groupEndDate) || ''}
              {settleView.reviewStartDate ? `　审核时间：${fmtD(settleView.reviewStartDate)} ~ ${fmtD(settleView.reviewEndDate)}` : ''}
            </div>
            <Table
              rowKey="dataId"
              resizable
              data={settleView.rows || []}
              hover
              size="medium"
              columns={settleColumnsData}
              empty={<span>暂无数据</span>}
            />
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <Button theme="default" variant="text" size="small" onClick={() => setSettleView(null)}>关闭</Button>
            </div>
          </div>
        )}
      </Dialog>

      {/* 撤回原因弹窗 */}
      <Dialog header="撤回对账函" visible={!!wd} footer={null} width="380px" closeOnOverlayClick onClose={() => setWd(null)}>
        <Textarea style={{ width: '100%' }} placeholder="请输入撤回原因（必填）" value={wdReason} onChange={(v) => setWdReason(v)} />
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Space>
            <Button theme="default" variant="text" size="small" onClick={() => setWd(null)}>取消</Button>
            <Button theme="danger" variant="text" size="small" loading={withdrawing} disabled={withdrawing} onClick={confirmWithdraw}>确定</Button>
          </Space>
        </div>
      </Dialog>

      {/* 行内「查看」：共享数据详情弹窗（与数据核对一致的三列弹窗） */}
      <ParttimeDataDetailDialog row={detailRow} onClose={() => setDetailRow(null)} />
    </div>
  );
}
